import React, { useState, useRef } from 'react';
import { 
  MessageSquare, 
  User as UserIcon, 
  Send, 
  Camera, 
  Heart, 
  MessageCircle, 
  Reply, 
  Edit3, 
  Trash2, 
  MoreHorizontal,
  Smile,
  Paperclip,
  X,
  Check,
  CheckCheck,
  RefreshCw
} from 'lucide-react';
import { useOfflineMessaging, OfflineMessage } from '../hooks/useOfflineMessaging';
import { supabase } from '../lib/supabaseClient';
import { offlineMessaging } from '../utils/offlineMessaging';
import { User } from '../types';

interface FarmerMessagesProps {
  user: User;
}

interface MessageActionsProps {
  message: Message;
  currentUserId: string;
  onEdit: (messageId: string, newText: string) => void;
  onDelete: (messageId: string) => void;
  onReply: (message: Message) => void;
  onReact: (messageId: string, reaction: string) => void;
  onComment: (messageId: string, comment: string) => void;
}

const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  currentUserId,
  onEdit,
  onDelete,
  onReply,
  onReact,
  onComment
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(message.message);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const reactions = ['❤️', '👍', '😂', '😮', '😢', '😡'];
  const isSender = message.sender_id === currentUserId;

  const handleEdit = () => {
    if (editText.trim() && editText !== message.message) {
      onEdit(message.id, editText.trim());
    }
    setEditMode(false);
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      onComment(message.id, newComment.trim());
      setNewComment('');
    }
  };

  const userReaction = message.reactions?.find(r => r.user_id === currentUserId);

  return (
    <div className="relative">
      {/* Message Actions */}
      <div className={`flex items-center gap-1 mt-1 ${isSender ? 'justify-end' : 'justify-start'}`}>
        <button
          onClick={() => setShowReactions(!showReactions)}
          className={`p-1 rounded-full hover:bg-gray-200 transition-colors ${
            userReaction ? 'text-red-500' : 'text-gray-500'
          }`}
          title="React"
        >
          <Heart className="w-3 h-3" fill={userReaction ? 'currentColor' : 'none'} />
        </button>
        
        <button
          onClick={() => setShowComments(!showComments)}
          className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
          title="Comment"
        >
          <MessageCircle className="w-3 h-3" />
        </button>
        
        <button
          onClick={() => onReply(message)}
          className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
          title="Reply"
        >
          <Reply className="w-3 h-3" />
        </button>
        
        {isSender && (
          <>
            <button
              onClick={() => setEditMode(true)}
              className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
              title="Edit"
            >
              <Edit3 className="w-3 h-3" />
            </button>
            
            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-1 rounded-full hover:bg-gray-200 transition-colors text-red-500"
              title="Delete"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>

      {/* Reactions Picker */}
      {showReactions && (
        <div className="absolute top-8 left-0 bg-white border rounded-lg shadow-lg p-2 flex gap-1 z-10">
          {reactions.map((reaction) => (
            <button
              key={reaction}
              onClick={() => {
                onReact(message.id, 'like');
                setShowReactions(false);
              }}
              className="p-1 hover:bg-gray-100 rounded text-lg"
            >
              {reaction}
            </button>
          ))}
        </div>
      )}

      {/* Edit Mode */}
      {editMode && (
        <div className="mt-2 p-2 bg-gray-50 rounded">
          <input
            type="text"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full p-2 border rounded text-sm"
            onKeyPress={(e) => e.key === 'Enter' && handleEdit()}
            autoFocus
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={handleEdit}
              className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
            >
              Save
            </button>
            <button
              onClick={() => {
                setEditMode(false);
                setEditText(message.message);
              }}
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Reactions Display */}
      {message.reactions && message.reactions.length > 0 && (
        <div className="flex gap-1 mt-1">
          {message.reactions.map((reaction) => (
            <span
              key={reaction.id}
              className="text-xs bg-gray-100 px-2 py-1 rounded-full flex items-center gap-1"
            >
              ❤️ {reaction.user_profile?.name || 'User'}
            </span>
          ))}
        </div>
      )}

      {/* Comments */}
      {showComments && (
        <div className="mt-2 p-2 bg-gray-50 rounded">
          <div className="space-y-2 mb-2">
            {message.comments?.map((comment) => (
              <div key={comment.id} className="text-xs">
                <span className="font-medium">{comment.user_profile?.name || 'User'}:</span>
                <span className="ml-1">{comment.comment}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 p-1 border rounded text-xs"
              onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
            />
            <button
              onClick={handleAddComment}
              className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
            >
              Post
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="absolute top-8 right-0 bg-white border rounded-lg shadow-lg p-3 z-10 min-w-48">
          <p className="text-sm text-gray-700 mb-3">Delete this message?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-3 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onDelete(message.id);
                setShowDeleteModal(false);
              }}
              className="px-3 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function FarmerMessages({ user }: FarmerMessagesProps) {
  const {
    messages,
    unreadCount,
    isOnline,
    syncing,
    sendMessage,
    markAsRead,
    editMessage,
    deleteMessage,
    loadOfflineMessages
  } = useOfflineMessaging(user?.id);

  const [newMessage, setNewMessage] = useState('');
  const [userProfile, setUserProfile] = useState<{
    id: string;
    avatar_url?: string;
    name?: string;
  } | null>(null);
  // Use the actual admin user ID from the system
  const adminUserId = '99af639e-82a2-4ad2-a172-d966800008fc'; // Real admin user ID from logs
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<OfflineMessage | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Load messages from database directly
  const [adminMessages, setAdminMessages] = useState<any[]>([]);
  const [realTimeUnreadCount, setRealTimeUnreadCount] = useState(0);
  
  const loadMessagesFromDB = async () => {
    if (!user?.id) return;
    
    try {
      console.log('🔄 FARMER: Loading messages between', user.id, 'and', adminUserId);
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${adminUserId}),and(sender_id.eq.${adminUserId},receiver_id.eq.${user.id})`)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      setAdminMessages(data || []);
      console.log('✅ FARMER: Loaded messages from DB:', data?.length || 0, 'messages');
    } catch (error) {
      console.error('❌ FARMER: Failed to load messages:', error);
    }
  };

  // Create conversations array for UI
  const conversations = [{
    id: 'admin-conversation',
    other_participant: {
      id: adminUserId,
      name: 'Admin Support', 
      email: 'kutloano.moshao111@gmail.com',
      avatar_url: null,
      is_online: true
    },
    unread_count: unreadCount,
    last_message: adminMessages[adminMessages.length - 1] || null
  }];

  const loadUserProfile = React.useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (data) {
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }, [user?.id]);

  const loadUnreadCount = async () => {
    if (!user?.id) return;
    
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', user.id)
        .eq('sender_id', adminUserId)
        .is('read_at', null);
      
      if (error) throw error;
      setRealTimeUnreadCount(count || 0);
    } catch (error) {
      console.error('❌ FARMER: Failed to load unread count:', error);
    }
  };

  // Load user profile, messages, and set up real-time updates
  React.useEffect(() => {
    if (user?.id) {
      loadUserProfile();
      loadMessagesFromDB();
      loadUnreadCount();
      
      // Set up polling for real-time updates
      const interval = setInterval(() => {
        loadMessagesFromDB();
        loadUnreadCount();
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [user?.id, loadUserProfile]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      console.log('📤 FARMER: Sending message to admin:', newMessage.trim());
      
      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          receiver_id: adminUserId,
          message: newMessage.trim(),
          parent_message_id: replyingTo?.id || null
        })
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ FARMER: Message sent:', data);
      
      // Reload messages
      await loadMessagesFromDB();
      setNewMessage('');
      setReplyingTo(null);
    } catch (error) {
      console.error('❌ FARMER: Failed to send message:', error);
    }
  };

  const handleMarkAsRead = async () => {
    try {
      await markAsRead(adminUserId);
      // Also update database for real-time sync with Dashboard
      if (user?.id) {
        await supabase
          .from('messages')
          .update({ read_at: new Date().toISOString() })
          .eq('receiver_id', user.id)
          .eq('sender_id', adminUserId)
          .is('read_at', null);
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const avatarUrl = await uploadAvatar(file);
        if (avatarUrl) {
          setUserProfile(prev => ({ ...prev, avatar_url: avatarUrl }));
        }
      } catch (error) {
        console.error('Error uploading avatar:', error);
        alert('Failed to upload avatar. Please try again.');
      }
    }
  };

  const handleFileAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditMessage = async (messageId: string, newText: string) => {
    try {
      await editMessage(messageId, newText);
    } catch (error) {
      console.error('Error editing message:', error);
      alert('Failed to edit message. Please try again.');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteMessage(messageId);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleReactToMessage = async (messageId: string, reaction: string) => {
    try {
      const existingReaction = messages
        .find(m => m.id === messageId)
        ?.reactions?.find(r => r.user_id === user.id);
      
      if (existingReaction) {
        await removeReaction(messageId, existingReaction.reaction_type);
      } else {
        await addReaction(messageId, 'like');
      }
    } catch (error) {
      console.error('Error reacting to message:', error);
    }
  };

  const handleCommentOnMessage = async (messageId: string, comment: string) => {
    try {
      // TODO: Implement comment functionality
      console.log('Comment added:', comment);
    } catch (error) {
      console.error('Error commenting on message:', error);
      alert('Failed to add comment. Please try again.');
    }
  };

  const handleSelectConversation = async (participantId: string) => {
    setSelectedConversation(participantId);
    // Mark messages as read when conversation is selected
    await handleMarkAsRead();
    // Dispatch event to reset count in Dashboard
    window.dispatchEvent(new CustomEvent('messagesRead'));
  };

  const uploadAvatar = async (file: File): Promise<string | null> => {
    // TODO: Implement avatar upload
    return null;
  };

  // Only mark as read when a conversation is actually selected
  // Remove auto-marking as read on component mount

  return (
    <div className="h-full flex flex-col">
      {/* Header with Profile and Sync Button */}
      <div className="flex items-center justify-between mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg relative">
        {isOnline && (
          <button
            onClick={async () => {
              try {
                await offlineMessaging.syncWithServer(supabase);
                await loadOfflineMessages();
              } catch (error) {
                console.error('Manual sync failed:', error);
              }
            }}
            className="absolute top-2 right-2 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            title="Sync messages"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
              {userProfile?.avatar_url ? (
                <img 
                  src={userProfile.avatar_url} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserIcon className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <button
              onClick={() => avatarInputRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white hover:bg-green-700 transition-colors"
              title="Change profile picture"
            >
              <Camera className="w-3 h-3" />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
            <p className="text-sm text-gray-600">
              {unreadCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mr-2">
                  {unreadCount} unread
                </span>
              )}
              Stay connected with the admin team
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <div className="lg:col-span-1">
          <h4 className="font-medium text-gray-900 mb-3">Conversations</h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {conversations.map((conversation) => {
              const isSelected = selectedConversation === conversation.other_participant?.id;
              
              return (
                <button
                  key={conversation.id}
                  onClick={() => handleSelectConversation(conversation.other_participant!.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                    isSelected
                      ? 'bg-green-50 border-green-200 shadow-sm'
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                          {conversation.other_participant?.avatar_url ? (
                            <img 
                              src={conversation.other_participant.avatar_url} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <UserIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        {conversation.other_participant?.is_online && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 truncate">
                          {conversation.other_participant?.name || 'Admin'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {conversation.other_participant?.email}
                        </p>
                      </div>
                    </div>
                    {conversation.unread_count > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                        {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                      </span>
                    )}
                  </div>
                  {conversation.last_message && (
                    <div className="mt-2 pl-13">
                      <p className="text-xs text-gray-500 truncate">
                        {conversation.last_message.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(conversation.last_message.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </button>
              );
            })}
            
            {conversations.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs">Start by sending a message to admin</p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-2">
          {selectedConversation ? (
            <div className="border border-gray-200 rounded-lg h-full flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                      {conversations.find(c => c.other_participant?.id === selectedConversation)?.other_participant?.avatar_url ? (
                        <img 
                          src={conversations.find(c => c.other_participant?.id === selectedConversation)?.other_participant?.avatar_url} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UserIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    {conversations.find(c => c.other_participant?.id === selectedConversation)?.other_participant?.is_online && (
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {conversations.find(c => c.other_participant?.id === selectedConversation)?.other_participant?.name || 'Admin'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {conversations.find(c => c.other_participant?.id === selectedConversation)?.other_participant?.is_online ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {adminMessages.map((message) => {
                  const isSender = message.sender_id === user.id;
                  return (
                    <div key={message.id} className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md`}>
                        {/* Reply indicator */}
                        {message.parent_message_id && (
                          <div className="text-xs text-gray-500 mb-1 pl-3 border-l-2 border-gray-300">
                            Replying to previous message
                          </div>
                        )}
                        
                        <div className={`px-4 py-2 rounded-lg ${
                          isSender
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}>
                          <p className="text-sm">{message.message}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className={`text-xs ${
                              isSender ? 'text-green-100' : 'text-gray-500'
                            }`}>
                              {new Date(message.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                              {message.edited_at && ' (edited)'}
                            </p>
                            {isSender && (
                              <div className="flex items-center gap-1">
                                {message.read_at ? (
                                  <CheckCheck className="w-3 h-3 text-green-200" />
                                ) : (
                                  <Check className="w-3 h-3 text-green-200" />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Message Actions */}
                        <MessageActions
                          message={message}
                          currentUserId={user.id}
                          onEdit={handleEditMessage}
                          onDelete={handleDeleteMessage}
                          onReply={setReplyingTo}
                          onReact={handleReactToMessage}
                          onComment={handleCommentOnMessage}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reply indicator */}
              {replyingTo && (
                <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Reply className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-800">
                        Replying to: {replyingTo.message.substring(0, 50)}...
                      </span>
                    </div>
                    <button
                      onClick={() => setReplyingTo(null)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Attachments preview */}
              {attachments.length > 0 && (
                <div className="px-4 py-2 bg-gray-50 border-t">
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 bg-white px-3 py-1 rounded border">
                        <span className="text-sm truncate max-w-32">{file.name}</span>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Attach file"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>
                  
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    maxLength={1000}
                  />
                  
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() && attachments.length === 0}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2 font-medium"
                  >
                    <Send className="w-4 h-4" />
                    Send
                  </button>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileAttachment}
                  className="hidden"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full border border-gray-200 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100">
              <div className="text-center text-gray-500">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-medium text-gray-700 mb-2">No Conversation Selected</h3>
                <p className="text-sm">Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}